// useWorkOrdersData.ts — Sprint 11. Owns the WorkOrders page's
// store-bound surface. The orchestrator no longer touches
// useCondoStore directly for any work-order mutation; it consumes
// the actions exposed here.
//
// Local UI state (which modal is open, which row is being acted on,
// AntD Form instances) stays in the orchestrator — it's tightly
// coupled to user interaction, not to the data layer.
import { useCallback, useEffect } from 'react';
import { staticApi } from '../../utils/staticApi';
import { useCondoStore } from '../../store/condoStore';
import type { WorkOrder } from '../../types';

export interface UseWorkOrdersDataArgs {
  condoId?: string;
}

export interface UseWorkOrdersData {
  workOrders: WorkOrder[];
  vendors:    any[];

  /** Create or update — pass `editingId` to update; omit for create. */
  saveOrder:     (payload: Partial<WorkOrder>, editingId?: string) => Promise<void>;
  /** Mark COMPLETED with the final invoice amount. */
  completeOrder: (id: string, actualCost: number) => Promise<void>;
  /** Status change for any other transition (Start, Cancel, etc.). */
  changeStatus:  (order: WorkOrder, newStatus: WorkOrder['status']) => Promise<void>;
  deleteOrder:   (id: string) => Promise<void>;
  assignVendor:  (id: string, vendorId: string, vendorName: string, vendorContact: string) => Promise<void>;
  updateCosts:   (id: string, laborCost: number, materialCost: number, invoiceNumber?: string) => Promise<void>;
  signOff:       (id: string, signedOffBy: string) => Promise<void>;
}

export function useWorkOrdersData({ condoId }: UseWorkOrdersDataArgs): UseWorkOrdersData {
  const {
    workOrders,
    vendors,
    fetchWorkOrders,
    createWorkOrder,
    updateWorkOrder,
    completeWorkOrder,
    deleteWorkOrder,
    assignWorkOrderVendor,
    signOffWorkOrder,
    updateWorkOrderCosts,
  } = useCondoStore() as any;

  // Initial load on mount / condo change.
  useEffect(() => {
    if (condoId) fetchWorkOrders(condoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condoId]);

  const saveOrder = useCallback(async (payload: Partial<WorkOrder>, editingId?: string) => {
    try {
      if (editingId) {
        await updateWorkOrder(editingId, payload);
        staticApi.message.success('Work order updated successfully');
      } else {
        await createWorkOrder(payload as WorkOrder);
        staticApi.message.success('Work order created successfully');
      }
    } catch {
      staticApi.message.error(editingId ? 'Failed to update work order' : 'Failed to create work order');
      throw new Error('saveOrder failed'); // surface to caller so the modal stays open
    }
  }, [createWorkOrder, updateWorkOrder]);

  const completeOrder = useCallback(async (id: string, actualCost: number) => {
    try {
      await completeWorkOrder(id, actualCost);
      staticApi.message.success('Work order marked as completed');
    } catch {
      staticApi.message.error('Failed to complete work order');
      throw new Error('completeOrder failed');
    }
  }, [completeWorkOrder]);

  const changeStatus = useCallback(async (order: WorkOrder, newStatus: WorkOrder['status']) => {
    try {
      await updateWorkOrder(order.id, { ...order, status: newStatus });
      staticApi.message.success(`Status updated to ${String(newStatus).replace('_', ' ')}`);
    } catch {
      staticApi.message.error('Failed to update status');
    }
  }, [updateWorkOrder]);

  const deleteOrder = useCallback(async (id: string) => {
    try {
      await deleteWorkOrder(id);
      staticApi.message.success('Work order deleted');
    } catch {
      staticApi.message.error('Failed to delete work order');
    }
  }, [deleteWorkOrder]);

  const assignVendor = useCallback(async (
    id: string, vendorId: string, vendorName: string, vendorContact: string,
  ) => {
    await assignWorkOrderVendor(id, vendorId, vendorName, vendorContact);
    staticApi.message.success('Vendor assigned');
  }, [assignWorkOrderVendor]);

  const updateCosts = useCallback(async (
    id: string, laborCost: number, materialCost: number, invoiceNumber?: string,
  ) => {
    await updateWorkOrderCosts(id, laborCost, materialCost, invoiceNumber);
    staticApi.message.success('Costs updated');
  }, [updateWorkOrderCosts]);

  const signOff = useCallback(async (id: string, signedOffBy: string) => {
    await signOffWorkOrder(id, signedOffBy);
    staticApi.message.success(`Work order signed off by ${signedOffBy}`);
  }, [signOffWorkOrder]);

  return {
    workOrders, vendors,
    saveOrder, completeOrder, changeStatus, deleteOrder,
    assignVendor, updateCosts, signOff,
  };
}
